import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../components/DashboardLayout";
import placeimage from "../image/noimage.jpg";
import { mediaAPI } from "../services/api";

export default function MediaPage() {
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.Auth);

    const [profileData, setProfileData] = useState({});
    const [formData, setFormData] = useState({
        photo: "",
        coverPhoto: "",
        gallery: [],
        videos: [],
    });

    const [loading, setLoading] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [newVideoUrl, setNewVideoUrl] = useState("");

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (!storedUser && !isAuthenticated) {
            navigate("/login");
            return;
        }

        // Fetch media data from backend
        const fetchMediaData = async () => {
            try {
                const response = await mediaAPI.getMedia();
                if (response.data.success) {
                    const mediaData = response.data.data;
                    setFormData({
                        photo: mediaData.photo || "",
                        coverPhoto: mediaData.coverPhoto || "",
                        gallery: mediaData.gallery || [],
                        videos: mediaData.videos || [],
                    });
                }
            } catch (error) {
                console.error("Error fetching media data:", error);
                // If error, try to load from localStorage as fallback
                if (storedUser) {
                    const userData = JSON.parse(storedUser);
                    setProfileData(userData);
                    setFormData({
                        photo: userData.photo || "",
                        coverPhoto: userData.coverPhoto || "",
                        gallery: userData.gallery || [],
                        videos: userData.videos || [],
                    });
                }
            }
        };

        fetchMediaData();
    }, [navigate, isAuthenticated]);

    const handleFileUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
        if (!validTypes.includes(file.type)) {
            toast.error("Please upload JPG, PNG, or WEBP images only");
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image size should not exceed 5MB");
            return;
        }

        setLoading(true);

        try {
            if (type === "profile") {
                const response = await mediaAPI.uploadProfilePhoto(file);
                console.log("Profile photo upload response:", response.data);

                if (response.data.success) {
                    const photoUrl = response.data.data.photo;
                    console.log("New photo URL:", photoUrl);

                    setFormData((prev) => ({
                        ...prev,
                        photo: photoUrl,
                    }));

                    // Update localStorage with new photo
                    const storedUser = localStorage.getItem("user");
                    if (storedUser) {
                        const userData = JSON.parse(storedUser);

                        // Update photo in both locations for consistency
                        userData.photo = photoUrl;
                        if (userData.get_trainer) {
                            userData.get_trainer.photo = photoUrl;
                        }

                        localStorage.setItem("user", JSON.stringify(userData));
                        console.log("Updated localStorage user:", userData);

                        // Small delay to ensure localStorage is updated
                        setTimeout(() => {
                            // Dispatch custom event to update sidebar
                            window.dispatchEvent(new Event("profilePhotoUpdated"));
                            console.log("Dispatched profilePhotoUpdated event");
                        }, 100);
                    }

                    toast.success("Profile photo uploaded successfully!");
                }
            } else if (type === "cover") {
                const response = await mediaAPI.uploadCoverPhoto(file);
                if (response.data.success) {
                    setFormData((prev) => ({
                        ...prev,
                        coverPhoto: response.data.data.coverPhoto,
                    }));
                    toast.success("Cover photo uploaded successfully!");
                }
            } else if (type === "gallery") {
                const response = await mediaAPI.uploadGalleryPhoto(file);
                if (response.data.success) {
                    setFormData((prev) => ({
                        ...prev,
                        gallery: [
                            ...prev.gallery,
                            {
                                id: response.data.data.id,
                                url: response.data.data.url,
                            },
                        ],
                    }));
                    toast.success("Photo added to gallery!");
                }
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            toast.error(error.response?.data?.message || "Failed to upload image. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveGalleryPhoto = async (photoId) => {
        if (window.confirm("Are you sure you want to remove this photo?")) {
            setLoading(true);
            try {
                const response = await mediaAPI.deleteGalleryPhoto(photoId);
                if (response.data.success) {
                    setFormData((prev) => ({
                        ...prev,
                        gallery: prev.gallery.filter((photo) => photo.id !== photoId),
                    }));
                    toast.success("Photo removed from gallery");
                }
            } catch (error) {
                console.error("Error deleting photo:", error);
                toast.error(error.response?.data?.message || "Failed to delete photo. Please try again.");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleAddVideo = async () => {
        if (!newVideoUrl.trim()) {
            toast.error("Please enter a video URL");
            return;
        }

        // Basic YouTube/Vimeo URL validation
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
        const vimeoRegex = /^(https?:\/\/)?(www\.)?vimeo\.com\/.+/;

        if (!youtubeRegex.test(newVideoUrl) && !vimeoRegex.test(newVideoUrl)) {
            toast.error("Please enter a valid YouTube or Vimeo URL");
            return;
        }

        const platform = youtubeRegex.test(newVideoUrl) ? "youtube" : "vimeo";

        setLoading(true);
        try {
            const response = await mediaAPI.addVideo(newVideoUrl, platform);
            if (response.data.success) {
                setFormData((prev) => ({
                    ...prev,
                    videos: [...prev.videos, response.data.data],
                }));
                setNewVideoUrl("");
                setShowVideoModal(false);
                toast.success("Video added successfully!");
            }
        } catch (error) {
            console.error("Error adding video:", error);
            toast.error(error.response?.data?.message || "Failed to add video. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveVideo = async (videoId) => {
        if (window.confirm("Are you sure you want to remove this video?")) {
            setLoading(true);
            try {
                const response = await mediaAPI.deleteVideo(videoId);
                if (response.data.success) {
                    setFormData((prev) => ({
                        ...prev,
                        videos: prev.videos.filter((video) => video.id !== videoId),
                    }));
                    toast.success("Video removed");
                }
            } catch (error) {
                console.error("Error deleting video:", error);
                toast.error(error.response?.data?.message || "Failed to delete video. Please try again.");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.photo) {
            toast.error("Please upload a profile photo");
            return;
        }

        if (formData.gallery.length < 3) {
            toast.error("Please add at least 3 photos to your gallery");
            return;
        }

        // All media is already uploaded via individual API calls
        // This is just a confirmation
        toast.success("All media has been saved successfully!");
    };

    const getCompletionPercentage = () => {
        let completed = 0;
        if (formData.photo) completed += 40;
        if (formData.coverPhoto) completed += 20;
        if (formData.gallery.length >= 3) completed += 40;
        return completed;
    };

    const completionPercentage = getCompletionPercentage();

    const getVideoEmbedUrl = (video) => {
        if (video.platform === "youtube") {
            const videoId = video.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (video.platform === "vimeo") {
            const videoId = video.url.match(/vimeo\.com\/(\d+)/)?.[1];
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return "";
    };

    return (
        <DashboardLayout activeTab="media">
            <div className="media-tab">
                <div className="tab-header">
                    <div>
                        <h3>
                            <i className="bi bi-images me-2"></i>
                            Media & Gallery
                        </h3>
                        <p className="text-muted">Upload photos and videos to showcase your work</p>
                    </div>
                    <div className="completion-badge">
                        <div className="mini-progress-circle">
                            <svg width="60" height="60">
                                <circle cx="30" cy="30" r="25" fill="none" stroke="#e9ecef" strokeWidth="5" />
                                <circle
                                    cx="30"
                                    cy="30"
                                    r="25"
                                    fill="none"
                                    stroke="#cc0000"
                                    strokeWidth="5"
                                    strokeDasharray={`${(completionPercentage / 100) * 157} 157`}
                                    transform="rotate(-90 30 30)"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="mini-percentage">{completionPercentage}%</div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="profile-form">
                    {/* Profile Photo */}
                    <div className="form-section">
                        <h5 className="section-title">
                            <i className="bi bi-person-circle me-2"></i>
                            Profile Photo <span className="text-danger">*</span>
                        </h5>
                        <p className="text-muted mb-3">Upload a professional photo (recommended: 400x400px)</p>

                        <div className="photo-upload-container">
                            <div className="photo-preview">
                                <img
                                    src={formData.photo || placeimage}
                                    alt="Profile"
                                    className="profile-photo-preview"
                                />
                            </div>
                            <div className="upload-actions">
                                <label htmlFor="profilePhoto" className="btn btn-danger">
                                    <i className="bi bi-upload me-2"></i>
                                    {formData.photo ? "Change Photo" : "Upload Photo"}
                                </label>
                                <input
                                    type="file"
                                    id="profilePhoto"
                                    accept="image/*"
                                    onChange={(e) => handleFileUpload(e, "profile")}
                                    style={{ display: "none" }}
                                />
                                <small className="text-muted d-block mt-2">
                                    JPG, PNG, or WEBP (Max 5MB)
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* Cover Photo */}
                    <div className="form-section">
                        <h5 className="section-title">
                            <i className="bi bi-image me-2"></i>
                            Cover Photo (Optional)
                        </h5>
                        <p className="text-muted mb-3">
                            Upload a cover photo for your profile (recommended: 1200x400px)
                        </p>

                        <div className="cover-photo-container">
                            {formData.coverPhoto ? (
                                <div className="cover-photo-preview">
                                    <img src={formData.coverPhoto} alt="Cover" />
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger remove-cover-btn"
                                        onClick={() => setFormData((prev) => ({ ...prev, coverPhoto: "" }))}
                                    >
                                        <i className="bi bi-trash"></i> Remove
                                    </button>
                                </div>
                            ) : (
                                <div className="cover-photo-placeholder">
                                    <i className="bi bi-image" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                                    <p className="text-muted mt-2">No cover photo uploaded</p>
                                </div>
                            )}
                            <label htmlFor="coverPhoto" className="btn btn-outline-danger mt-3">
                                <i className="bi bi-upload me-2"></i>
                                {formData.coverPhoto ? "Change Cover Photo" : "Upload Cover Photo"}
                            </label>
                            <input
                                type="file"
                                id="coverPhoto"
                                accept="image/*"
                                onChange={(e) => handleFileUpload(e, "cover")}
                                style={{ display: "none" }}
                            />
                        </div>
                    </div>

                    {/* Gallery */}
                    <div className="form-section">
                        <div className="section-header-with-action">
                            <div>
                                <h5 className="section-title">
                                    <i className="bi bi-images me-2"></i>
                                    Photo Gallery <span className="text-danger">*</span>
                                </h5>
                                <p className="text-muted">Add at least 3 photos showcasing your training (minimum 3 required)</p>
                            </div>
                            <label htmlFor="galleryPhoto" className="btn btn-danger">
                                <i className="bi bi-plus-circle me-2"></i>
                                Add Photo
                            </label>
                            <input
                                type="file"
                                id="galleryPhoto"
                                accept="image/*"
                                onChange={(e) => handleFileUpload(e, "gallery")}
                                style={{ display: "none" }}
                            />
                        </div>

                        {formData.gallery.length > 0 ? (
                            <div className="gallery-grid">
                                {formData.gallery.map((photo) => (
                                    <div key={photo.id} className="gallery-item">
                                        <img src={photo.url} alt={photo.name} />
                                        <div className="gallery-item-overlay">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleRemoveGalleryPhoto(photo.id)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <i className="bi bi-images" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                                <p className="text-muted mt-2">No photos in gallery</p>
                                <p className="text-muted">Click "Add Photo" to upload images</p>
                            </div>
                        )}

                        {formData.gallery.length > 0 && (
                            <div className="gallery-count mt-3">
                                <i className="bi bi-check-circle-fill text-success me-2"></i>
                                <strong>{formData.gallery.length}</strong> photo{formData.gallery.length > 1 ? "s" : ""} in gallery
                                {formData.gallery.length < 3 && (
                                    <span className="text-danger ms-2">
                                        ({3 - formData.gallery.length} more needed)
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Videos */}
                    <div className="form-section">
                        <div className="section-header-with-action">
                            <div>
                                <h5 className="section-title">
                                    <i className="bi bi-play-circle me-2"></i>
                                    Training Videos (Optional)
                                </h5>
                                <p className="text-muted">Add YouTube or Vimeo videos</p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => setShowVideoModal(true)}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Add Video
                            </button>
                        </div>

                        {formData.videos.length > 0 ? (
                            <div className="videos-grid">
                                {formData.videos.map((video) => (
                                    <div key={video.id} className="video-item">
                                        <div className="video-embed">
                                            <iframe
                                                src={getVideoEmbedUrl(video)}
                                                title="Training Video"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger mt-2 w-100"
                                            onClick={() => handleRemoveVideo(video.id)}
                                        >
                                            <i className="bi bi-trash me-2"></i>
                                            Remove Video
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <i className="bi bi-play-circle" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                                <p className="text-muted mt-2">No videos added</p>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn btn-danger btn-lg"
                            disabled={loading || completionPercentage < 80}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle me-2"></i>
                                    Save Media
                                </>
                            )}
                        </button>
                        {completionPercentage < 80 && (
                            <p className="text-muted mt-2 mb-0">
                                <i className="bi bi-info-circle me-1"></i>
                                Upload profile photo and at least 3 gallery photos to save
                            </p>
                        )}
                    </div>
                </form>

                {/* Video Modal */}
                {showVideoModal && (
                    <div className="modal-overlay" onClick={() => setShowVideoModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h5>
                                    <i className="bi bi-play-circle me-2"></i>
                                    Add Training Video
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowVideoModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="videoUrl" className="form-label">
                                        Video URL (YouTube or Vimeo)
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        id="videoUrl"
                                        value={newVideoUrl}
                                        onChange={(e) => setNewVideoUrl(e.target.value)}
                                        placeholder="https://www.youtube.com/watch?v=..."
                                    />
                                    <small className="text-muted">
                                        Paste the full URL of your YouTube or Vimeo video
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowVideoModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-danger" onClick={handleAddVideo}>
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add Video
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
